import { UIEvent, useRef, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'

import EmptyView from 'components/EmptyView'
import { FixMeAny } from 'types/global'

export type VirtualizedListScrollEvent = (
  e: UIEvent<HTMLDivElement, globalThis.UIEvent>,
  isBottomReached: boolean,
) => void

type VirtualizeListProps = {
  rowCount: number
  rowGetter: (index: number) => FixMeAny
  onScroll?: VirtualizedListScrollEvent
  emptyMessage?: string
  emptyView?: React.ReactNode
  showEmptyView?: boolean
  height?: string
  width?: string
  initialScrollIndex?: number
  estimateSize?: () => number
  paddingStart?: number
  paddingEnd?: number
  overscan?: number
}

const VirtualizedList = ({
  rowCount,
  rowGetter,
  onScroll,
  emptyMessage,
  emptyView,
  showEmptyView = true,
  height,
  width,
  initialScrollIndex,
  paddingStart,
  paddingEnd,
  estimateSize = () => 50,
  overscan,
}: VirtualizeListProps) => {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize,
    paddingStart,
    paddingEnd,
    overscan,
  })
  const items = virtualizer.getVirtualItems()

  useEffect(() => {
    let initialized = false

    if (parentRef.current && initialScrollIndex && !initialized) {
      virtualizer.scrollToIndex(initialScrollIndex)
      initialized = true
    }
  }, [initialScrollIndex, rowCount, virtualizer])

  if (rowCount <= 0 && !showEmptyView) {
    return <></>
  }

  if (rowCount <= 0 && emptyView && showEmptyView) {
    return <>{emptyView}</>
  }

  if (rowCount <= 0) {
    return <EmptyView message={emptyMessage} />
  }

  return (
    <div
      ref={parentRef}
      style={{
        height: height ?? '100%',
        width: width ?? '100%',
        overflowY: 'auto',
        contain: 'strict',
      }}
      onScroll={(e) => {
        const isBottomReached =
          e.currentTarget.scrollHeight - e.currentTarget.scrollTop <= e.currentTarget.clientHeight + 2 // Add 2px for rounding error
        onScroll?.(e, isBottomReached)
      }}
    >
      <div
        style={{
          height: virtualizer.getTotalSize() - 1,
          width: '100%',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${items[0]?.start ?? 0}px)`,
          }}
        >
          {items.map((virtualRow) => (
            <div
              key={virtualRow.key.toString()}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              className={virtualRow.index % 2 ? 'list-item-odd' : 'list-item-even'}
            >
              {rowGetter(virtualRow.index)}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default VirtualizedList
